import { useState, useEffect, useRef } from "react";
import { useApp } from "../contexts/AppContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useCreateTemplateMutation } from "../store/api/templates";
import { toast } from "sonner";
import { handleApiError } from "../utils/apiErrors";
import { RepeatType, Task } from "../types";
import * as yup from "yup";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2 } from "lucide-react";

interface CreateTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CreateTemplateModal({
  open,
  onOpenChange,
}: CreateTemplateModalProps) {
  const { t } = useLanguage();
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const [createTemplate, { isLoading }] = useCreateTemplateMutation();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tasks, setTasks] = useState<Omit<Task, "id" | "workspaceId" | "createdAt" | "updatedAt">[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [taskFormErrors, setTaskFormErrors] = useState<{ [key: string]: string }>({});

  const [editingTask, setEditingTask] = useState<Partial<Omit<Task, "id" | "workspaceId" | "createdAt" | "updatedAt">>>(
    {
      name: "",
      description: "",
      recurrence: { type: "none" },
    }
  );

  // Yup validation schema
  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .required(t('settings.templateNameRequiredError'))
      .max(50, t('settings.templateNameMaxLength')),
    description: yup
      .string()
      .max(150, t('settings.templateDescMaxLength')),
  });

  // Task validation schema
  const taskValidationSchema = yup.object().shape({
    name: yup
      .string()
      .required(t('settings.taskNameRequiredError'))
      .max(50, t('settings.taskNameMaxLength')),
    description: yup
      .string()
      .max(150, t('settings.taskDescMaxLength')),
  });

  const validateForm = async (): Promise<boolean> => {
    try {
      await validationSchema.validate({ name, description }, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const validateAll = async (): Promise<boolean> => {
    const fieldValid = await validateForm();
    const formValid = tasks.length > 0;

    return fieldValid && formValid;
  };

  const validateTaskForm = async (): Promise<boolean> => {
    try {
      await taskValidationSchema.validate(editingTask, { abortEarly: false });
      setTaskFormErrors({});
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setTaskFormErrors(newErrors);
      }
      return false;
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setTasks([]);
      setErrors({});
      setTaskFormErrors({});
      setEditingTask({
        name: "",
        description: "",
        recurrence: { type: "none" },
      });
    }
  }, [open, t]);

  // Scroll to top when modal opens
  useEffect(() => {
    if (open && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open]);

  const handleAddTask = async () => {
    const isValid = await validateTaskForm();
    if (!isValid) {
      return;
    }

    const newTask: Omit<Task, "id" | "workspaceId" | "createdAt" | "updatedAt"> = {
      name: editingTask.name?.trim() || '',
      description: editingTask.description || "",
      recurrence: editingTask.recurrence || { type: "none" },
      daysUntilDue: 0,
    };

    setTasks([...tasks, newTask]);
    
    setEditingTask({
      name: "",
      description: "",
      recurrence: { type: "none" },
    });
  };

  const handleRemoveTask = (index: number) => {
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!currentWorkspace) {
      toast.error(t('workspace.noWorkspaceSelected'));
      return;
    }

    // Validate all (fields + business rules)
    const isValid = await validateAll();
    if (!isValid) {
      return;
    }

    try {
      // Send tasks as inline objects - backend will create them
      await createTemplate({
        workspaceId: currentWorkspace.id,
        data: {
          name: name.trim(),
          description: description.trim(),
          tasks: tasks.map(task => ({
            name: task.name,
            description: task.description || '',
            recurrence: task.recurrence,
            daysUntilDue: task.daysUntilDue || 0,
          })) as any[],
        },
      }).unwrap();

      toast.success(t('settings.templateCreated') || 'Template created successfully');
      
      // Reset form state
      setName("");
      setDescription("");
      setTasks([]);
      setEditingTask({
        name: "",
        description: "",
        recurrence: { type: "none" },
      });
      
      onOpenChange(false);
    } catch (error) {
      handleApiError(error, t('settings.templateCreateFailed') || 'Failed to create template');
    }
  };

  const getRecurrenceLabel = (type: string) => {
    switch (type) {
      case "none": return t('tasks.none');
      case "daily": return t('tasks.daily');
      case "weekly": return t('tasks.weekly');
      case "monthly": return t('tasks.monthly');
      case "quarterly": return t('tasks.quarterly');
      case "semi-annually": return t('tasks.semiAnnually');
      case "yearly": return t('tasks.yearly');
      default: return type;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('settings.newTaskTemplate')}</DialogTitle>
          <DialogDescription>{t('settings.createTemplateDesc')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Template Info */}
          <div className="space-y-4">
            <div>
              <Label className="mb-2">{t('settings.templateNameRequired')}</Label>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  // Clear error when user types
                  if (errors.name) {
                    setErrors({ ...errors, name: '' });
                  }
                }}
                placeholder={t('settings.templateNamePlaceholder')}
                required
                maxLength={50}
                className={errors.name ? "border-red-500" : ""}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}             
            </div>

            <div>
              <Label className="mb-2">{t('common.description')}</Label>
              <Textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  // Clear error when user types
                  if (errors.description) {
                    setErrors({ ...errors, description: '' });
                  }
                }}
                placeholder={t('settings.templateDescPlaceholder')}
                maxLength={150}
                className={errors.description ? "border-red-500" : ""}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description}</p>
              )}
            </div>
          </div>

          {/* Task List */}
          <div className="space-y-3">
            <Label className="mb-2">{t('settings.registeredTasks')} ({tasks.length}{t('settings.items')})</Label>
            {tasks.length > 0 && (
              <div className="space-y-2">
                {tasks.map((task, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="text-sm text-gray-900">{task.name}</div>
                      {task.description && (
                        <div className="text-xs text-gray-600 mt-0.5">
                          {task.description}
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                        <span>{getRecurrenceLabel(task.recurrence.type)}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveTask(index)}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Task Form */}
          <div className="border-t pt-6 space-y-4">
            <Label className="mb-2">{t('settings.addNewTask')}</Label>
            
            <div>
              <Label className="mb-2">{t('settings.taskNameRequired')}</Label>
              <Input
                value={editingTask.name || ""}
                onChange={(e) => {
                  setEditingTask({ ...editingTask, name: e.target.value });
                  // Clear error when user types
                  if (taskFormErrors.name) {
                    setTaskFormErrors({ ...taskFormErrors, name: '' });
                  }
                }}
                placeholder={t('settings.taskNamePlaceholder')}
                maxLength={50}
                className={taskFormErrors.name ? "border-red-500" : ""}
              />
              {taskFormErrors.name && (
                <p className="mt-1 text-sm text-red-500">{taskFormErrors.name}</p>
              )}
            </div>

            <div>
              <Label className="mb-2">{t('tasks.taskSummary')}</Label>
              <Textarea
                value={editingTask.description || ""}
                onChange={(e) => {
                  setEditingTask({ ...editingTask, description: e.target.value });
                  // Clear error when user types
                  if (taskFormErrors.description) {
                    setTaskFormErrors({ ...taskFormErrors, description: '' });
                  }
                }}
                placeholder={t('settings.taskDescPlaceholder')}
                rows={3}
                maxLength={150}
                className={taskFormErrors.description ? "border-red-500" : ""}
              />
              {taskFormErrors.description && (
                <p className="mt-1 text-sm text-red-500">{taskFormErrors.description}</p>
              )}
            </div>

            <div>
              <Label className="mb-2">{t('settings.recurrenceSetting')}</Label>
              <Select
                value={editingTask.recurrence?.type}
                onValueChange={(value: "none" | "daily" | "weekly" | "monthly" | "quarterly" | "semi-annually" | "yearly") =>
                  setEditingTask({
                    ...editingTask,
                    recurrence: { type: value },
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('tasks.none')}</SelectItem>
                  <SelectItem value="daily">{t('tasks.daily')}</SelectItem>
                  <SelectItem value="weekly">{t('tasks.weekly')}</SelectItem>
                  <SelectItem value="monthly">{t('tasks.monthly')}</SelectItem>
                  <SelectItem value="quarterly">{t('tasks.quarterly')}</SelectItem>
                  <SelectItem value="semi-annually">{t('tasks.semiAnnually')}</SelectItem>
                  <SelectItem value="yearly">{t('tasks.yearly')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={handleAddTask} className="w-full" type="button">
              <Plus className="w-4 h-4" />
              {t('settings.addTask')}
            </Button>
          </div>

          {/* Action Buttons */}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                // Reset form when canceling
                setName("");
                setDescription("");
                setTasks([]);
                setTaskFormErrors({});
                setEditingTask({
                  name: "",
                  description: "",
                  recurrence: { type: "none" },
                });
                onOpenChange(false);
              }} 
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isLoading || !name.trim() || tasks.length === 0}
            >
              {isLoading ? t('common.saving') || 'Saving...' : t('common.save')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
